import React, {useEffect, useRef, useState} from 'react';
import styles from './VideoEditorUI.module.css';
import PageTitle from '../../../uiToolkit/PageTitle';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import TipBox from '../../../uiToolkit/TipBox';
import ErrorBox from '../../../uiToolkit/ErrorBox';
import Timeline from '../components/Timeline';
import {EditChunkOption} from '../../../data/repositories/LibraryRepository';
import {VideoEditorText} from '../VideoEditorText';
import FormattedText from '../../../uiToolkit/FormattedText/FormattedText';
import ChunkList from '../components/ChunkList';
import ChunkPreview from '../components/ChunkPreview';
import {VideoEditorState} from '../store/VideoEditorStore';
import BackButton from '../../../uiToolkit/Buttons/BackButton';
import ChunkListOverlay from '../components/ChunkListOverlay';

interface Props {
  onBack: () => void;
  chunks: GeneratedVideoChunk[];
  isLoading: boolean;
  state: VideoEditorState;
  onEditChunk: (option: EditChunkOption, chunkId: string) => void;
  refreshChunks: () => void;
  editChunkErrorCode?: string;
  clearErrors: () => void;
  onChunksReorder: (chunks: GeneratedVideoChunk[]) => void;
}

const EditVideoSourceUI: React.FC<Props> = ({
  onBack,
  chunks,
  isLoading,
  state,
  onEditChunk,
  refreshChunks,
  editChunkErrorCode,
  clearErrors,
  onChunksReorder,
}: Props) => {
  const defaultPixelsPerSecond = 5;
  const divRef = useRef<HTMLDivElement | null>(null);
  const [selectedChunk, setSelectedChunk] = useState<GeneratedVideoChunk>();
  const [totalTimelineSeconds, setTotalTimelineSeconds] = useState(0);
  const [pixelsPerSecond, setPixelsPerSecond] = useState(
    defaultPixelsPerSecond,
  );
  const [stepInSeconds, setStepInSeconds] = useState(30);
  const [intervals, setIntervals] = useState(6);

  useEffect(() => {
    if (!selectedChunk && chunks.length > 0) {
      setSelectedChunk(chunks[0]);
    }
    calibrateZoom();
    const handleResize = () => {
      calibrateZoom();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chunks]);

  const calibrateZoom = () => {
    const total = chunks.reduce((sum, x) => sum + x.duration, 0);
    setTotalTimelineSeconds(total);
    let doubleZoom = false;
    if (divRef.current && chunks.length > 0) {
      const rect = divRef.current.getBoundingClientRect();
      const clientRectWidth = rect.width;
      doubleZoom =
        total > 0 && clientRectWidth >= total * defaultPixelsPerSecond * 2;
      setPixelsPerSecond(doubleZoom ? 10 : 5);
      setStepInSeconds(doubleZoom ? 15 : 30);
      setIntervals(doubleZoom ? 3 : 6);
    }
  };

  const selectChunk = (chunkId: string) => {
    setSelectedChunk(chunks.find(x => x.id === chunkId) || undefined);
  };

  return (
    <div ref={divRef} className={styles.videoEditorContainer}>
      <PageTitle title={VideoEditorText.title} />
      <div className={styles.headLine}>
        <BackButton overrideOnBack={onBack} />
        <h5>{VideoEditorText.preview}</h5>
      </div>
      <ChunkPreview selectedChunk={selectedChunk} />
      <ErrorBox
        errorCodes={editChunkErrorCode ? [editChunkErrorCode] : []}
        clearErrors={clearErrors}
      />
      <div className={styles.chunksOverlay}>
        <div
          className={`${styles.chunksContainer} ${
            state !== VideoEditorState.Idle ? styles.busy : ''
          }`}>
          <Timeline
            endTime={totalTimelineSeconds}
            step={stepInSeconds}
            pixelsPerSecond={pixelsPerSecond}
            intervals={intervals}
          />
          <ChunkList
            chunks={chunks}
            selectedChunk={selectedChunk}
            onEditChunk={onEditChunk}
            onSelectChunk={selectChunk}
            onChunksReorder={onChunksReorder}
            totalTimelineSeconds={totalTimelineSeconds}
            pixelsPerSecond={pixelsPerSecond}
          />
        </div>
        <ChunkListOverlay
          state={state}
          isLoading={isLoading}
          refreshChunks={refreshChunks}
        />
      </div>
      <TipBox
        noStandardHeader={true}
        tip={
          <>
            <div className="tipHeader">{VideoEditorText.tipHeader}</div>
            <div className="tipContent">
              <ul>
                <li>
                  <FormattedText text={VideoEditorText.tip1} />
                </li>
                <li>
                  <FormattedText text={VideoEditorText.tip2} />
                </li>
                <li>
                  <FormattedText text={VideoEditorText.tip3} />
                </li>
                <li>
                  <FormattedText text={VideoEditorText.tip4} />
                </li>
              </ul>
            </div>
          </>
        }
      />
    </div>
  );
};

export default EditVideoSourceUI;
